// Driver "Hot zones" demand heat map on the portal Dashboard map.
//
// Same snapshot the drivers read (/demand_heat, written by publishDemandHeat
// every 10 min), drawn on the Dashboard's EXISTING Google Maps instance. Cost
// posture, in this order:
//   • Maps JS: zero extra map loads — Google bills per map instantiation, not
//     per overlay. Never create a second map for this.
//   • RTDB: one-shot get() of demand_heat/s + demand_heat/f (~5 KB) when the
//     layer is switched on, then every 10 min while the browser tab is
//     visible, plus a manual Refresh spaced ≥ 60 s apart. No onValue listener:
//     a listener would cost publishes × open tabs and resurrect the stale-tab
//     problem the portal already fought once.
//   • Admins also read demand_heat/_meta (~0.3 KB) for the health line, so the
//     publisher can be checked without opening the Firebase console.
//
// Rendering: google.maps.visualization.HeatmapLayer was REMOVED from the Maps
// JavaScript API (gone in v3.65; the weekly channel the portal loads ships a
// constructor that throws), so the heat is painted by the canvas OverlayView
// in ./demandHeatCanvas.js — core API only, no new dependency.
//
// Exports: useDemandSnapshot (data + refresh policy), decodeDemandSnapshot
// (codec → cells/zones for one tab), DemandHeatLayer (imperative overlay,
// renders null), DemandZonesCard (the floating card), zonePinIcon (SVG pin
// data-URL for the rank markers the Dashboard renders as <Marker> children).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { api } from 'common';
import * as demandHeatCodecNs from 'common/src/other/demandHeatCodec';
import { FONT_FAMILY } from 'common/sharedFunctions';
import { makeHeatOverlay, HEAT_OPACITY } from './demandHeatCanvas';

// The codec is a CommonJS module shared with Cloud Functions; webpack exposes
// it either as the namespace itself or under `default` depending on interop.
// It must stay plain ES5 (no spread / class / async): a runtime helper import
// would flip it to an ES module and break `module.exports`.
const demandHeatCodec = demandHeatCodecNs.default || demandHeatCodecNs;

const REFRESH_MS = 10 * 60e3;   // the publisher cadence — reading faster buys nothing
const MANUAL_GAP_MS = 60e3;     // Refresh button spacing once data exists
const MAX_ROWS = 3;             // same cap as the driver app (MAX_ZONE_ROWS)

const LEVEL_STYLE = {
    very: { bg: '#FDE4DC', fg: '#B3200E', dot: '#B3200E' },
    busy: { bg: '#FFF3D6', fg: '#8A5A00', dot: '#FFB300' },
    warming: { bg: '#FFF9DB', fg: '#6B5A00', dot: '#FFE27A' },
};
const ORANGE = '#F1501A';
const INK = '#1D292B';
const MUTED = '#696969';       // 5.5:1 on white — AA for 11-12 px text
const TREND_UP = '#0A7A02';    // 5.5:1 on white (the theme green is 3.1:1)
const EVENT_BLUE = '#0B5CAD';  // AA on white — Next-hour zone seeded by a city event's expected crowd

// ---------------------------------------------------------------------------
// Data: one-shot reads on a 10-min visible-tab cadence.
// ---------------------------------------------------------------------------
export function useDemandSnapshot({ enabled, withMeta }) {
    const [state, setState] = useState({ snap: null, meta: null, status: 'idle', fetchedAt: 0 });
    const [busy, setBusy] = useState(false);
    const [manualAt, setManualAt] = useState(0);
    const aliveRef = useRef(true);
    const inFlightRef = useRef(false);
    const lastManualRef = useRef(0);
    const attemptAtRef = useRef(0);     // last ATTEMPT (success or failure) — gates automatic reads
    const fetchedAtRef = useRef(0);     // last SUCCESS — the manual cooldown applies only once data exists
    const deniedRef = useRef(false);    // a rules rejection is terminal for automatic reads

    const load = useCallback(async (manual) => {
        if (!enabled || inFlightRef.current) return;
        if (deniedRef.current && !manual) return;
        const now = Date.now();
        if (manual) {
            // Space out re-reads of data we already have; a Retry after a failed
            // first read is the user asking for the read that never happened.
            if (fetchedAtRef.current && now - lastManualRef.current < MANUAL_GAP_MS) return;
            lastManualRef.current = now;
            setManualAt(now);
        }
        inFlightRef.current = true;
        attemptAtRef.current = now;
        setBusy(true);
        setState((prev) => (prev.snap ? prev : { ...prev, status: 'loading' }));
        const [res, meta] = await Promise.all([
            api.fetchDemandHeat('full'),
            withMeta ? api.fetchDemandHeatMeta() : Promise.resolve(null),
        ]);
        inFlightRef.current = false;
        if (!aliveRef.current) return;
        setBusy(false);
        if (res && res.error) {
            deniedRef.current = res.error === 'permission_denied';
            setState((prev) => ({
                ...prev,
                status: deniedRef.current ? 'denied' : (prev.snap ? 'stale' : 'error'),
            }));
            return;
        }
        deniedRef.current = false;
        fetchedAtRef.current = now;
        const s = res ? res.s : null;
        const usable = demandHeatCodec.isUsableSummary(s);
        setState({
            snap: usable ? { s, f: res.f || null } : null,
            meta: meta || null,
            status: !usable ? 'empty' : (s.off === true ? 'off' : 'ok'),
            fetchedAt: now,
        });
    }, [enabled, withMeta]);

    useEffect(() => {
        aliveRef.current = true;
        return () => { aliveRef.current = false; };
    }, []);

    useEffect(() => {
        // The last snapshot is kept when the switch goes off (the Dashboard
        // hides the layer, card and pins), so switching back on inside the
        // publisher cadence costs no read.
        if (!enabled) return undefined;
        // One predicate for every automatic read (switch-on, 10-min tick, tab
        // shown again): tab visible AND last attempt older than the cadence.
        const due = () => document.visibilityState === 'visible' && Date.now() - attemptAtRef.current >= REFRESH_MS;
        const tick = () => { if (due()) load(false); };
        tick();
        const id = setInterval(tick, REFRESH_MS);
        document.addEventListener('visibilitychange', tick);
        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', tick);
        };
    }, [enabled, load]);

    const refresh = useCallback(() => load(true), [load]);
    return { snap: state.snap, meta: state.meta, status: state.status, fetchedAt: state.fetchedAt, busy, manualAt, refresh };
}

// snapshot {s, f} + tab 'n' | 'x' → { cells, zones, quiet }
export function decodeDemandSnapshot(snap, tab) {
    const key = tab === 'x' ? 'x' : 'n';
    if (!snap || !snap.s) return { cells: [], zones: [], quiet: false };
    const cells = demandHeatCodec.decodeCells(snap.f ? snap.f[key] : null, snap.s.g);
    const raw = Array.isArray(snap.s[key]) ? snap.s[key] : [];
    const zones = raw.map(demandHeatCodec.zoneFromTuple).filter(Boolean).slice(0, MAX_ROWS);
    return { cells, zones, quiet: key === 'n' && snap.s.q === 1 };
}

// ---------------------------------------------------------------------------
// Heat overlay on the existing map (imperative; the React tree renders null).
// ---------------------------------------------------------------------------
export function DemandHeatLayer({ map, cells, visible }) {
    const overlayRef = useRef(null);
    const brokenRef = useRef(false);

    useEffect(() => {
        const g = window.google && window.google.maps;
        if (!map || !g || typeof g.OverlayView !== 'function' || brokenRef.current) return;
        try {
            if (!overlayRef.current) {
                overlayRef.current = makeHeatOverlay(g);
                overlayRef.current.setMap(map);
            }
            overlayRef.current.setCells(visible ? cells : [], HEAT_OPACITY);
        } catch (e) {
            // Never let a Maps API change take the Dashboard down: pins and the
            // card keep working without the heat.
            brokenRef.current = true;
            try { if (overlayRef.current) overlayRef.current.setMap(null); } catch (e2) { /* ignore */ }
            overlayRef.current = null;
            console.warn('[DemandHeatLayer] heat overlay unavailable:', e && e.message);
        }
    }, [map, cells, visible]);

    useEffect(() => () => {
        if (overlayRef.current) {
            try { overlayRef.current.setMap(null); } catch (e) { /* ignore */ }
            overlayRef.current = null;
        }
    }, []);

    return null;
}

// Orange teardrop with the rank digit, as an SVG data URL for <Marker icon>.
export const zonePinIcon = (rank) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 44" width="36" height="44">` +
        `<path d="M18 1C9.2 1 2.5 7.7 2.5 16.5c0 10.4 13.2 24.6 14.3 25.8a1.6 1.6 0 0 0 2.4 0C20.3 41.1 33.5 26.9 33.5 16.5 33.5 7.7 26.8 1 18 1z" fill="${ORANGE}" stroke="#fff" stroke-width="2"/>` +
        `<text x="18" y="22" text-anchor="middle" font-family="Roboto, Arial, sans-serif" font-weight="700" font-size="15" fill="#fff">${rank}</text></svg>`;
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

// ---------------------------------------------------------------------------
// The floating card: title, tabs, up to 3 zones, legend, health line.
// ---------------------------------------------------------------------------
const Flame = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill={ORANGE} d="M12 2s1 3 4 6c2.4 2.4 4 4.6 4 8a8 8 0 0 1-16 0c0-2.3.9-4.1 2.3-5.6.4 1.9 1.5 2.8 2.7 2.8C10.5 9.5 10 5.5 12 2z" />
    </svg>
);

const Pill = ({ level, label }) => {
    const st = LEVEL_STYLE[level] || LEVEL_STYLE.warming;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: st.bg, color: st.fg, borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700, fontFamily: FONT_FAMILY }}>
            <span style={{ width: 8, height: 8, borderRadius: 4, background: st.dot, display: 'inline-block' }} />
            {label}
        </span>
    );
};

export function DemandZonesCard({ status, snap, meta, zones, quiet, tab, onTab, onRefresh, busy, manualAt, fetchedAt, isAdmin, style }) {
    const { t } = useTranslation();
    const L = (key, dflt, opts) => {
        const v = t(key, opts ? { ...opts, defaultValue: dflt } : { defaultValue: dflt });
        return v === key ? dflt : v;
    };
    // "Updated N min ago" and the Refresh cooldown tick without re-reading anything.
    const [, forceTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => forceTick((n) => n + 1), 15e3);
        return () => clearInterval(id);
    }, []);

    const s = snap ? snap.s : null;
    const ageMin = s && Number.isFinite(Number(s.at)) ? Math.max(0, Math.round((Date.now() - Number(s.at)) / 60e3)) : null;
    const nextHour = useMemo(() => {
        if (!s || !Number.isFinite(Number(s.slot))) return null;
        return moment().hour((Number(s.slot) % 24 + 1) % 24).minute(0).format('h a');
    }, [s]);
    const levelWord = (lv) => {
        const w = demandHeatCodec.levelWord(lv);
        return w === 'very' ? L('dash_demand_level_very', 'Very busy') : (w === 'busy' ? L('dash_demand_level_busy', 'Busy') : L('dash_demand_level_warming', 'Warming'));
    };
    const trendText = (tr) => (tr > 0
        ? { text: '↑ ' + L('dash_demand_trend_up', 'Above usual'), color: TREND_UP }
        : (tr < 0 ? { text: '↓ ' + L('dash_demand_trend_down', 'Below usual'), color: MUTED }
            : { text: '→ ' + L('dash_demand_trend_flat', 'Usual'), color: MUTED }));
    const driversText = (n) => (n === 1
        ? L('dash_demand_driver_one', '{{n}} driver there', { n: 1 })
        : L('dash_demand_drivers', '{{n}} drivers there', { n }));

    // The refresh cooldown applies only once data exists; a Retry with nothing
    // loaded always goes through (the hook enforces the same rule).
    const coolingUntil = fetchedAt && manualAt ? manualAt + MANUAL_GAP_MS : 0;
    const coolSecs = coolingUntil > Date.now() ? Math.ceil((coolingUntil - Date.now()) / 1000) : 0;
    const refreshDisabled = busy || coolSecs > 0;
    // Live countdown while cooling, and a precise re-enable at the end.
    useEffect(() => {
        if (!coolingUntil || coolingUntil <= Date.now()) return undefined;
        const id = setInterval(() => forceTick((n) => n + 1), 1000);
        const end = setTimeout(() => forceTick((n) => n + 1), coolingUntil - Date.now() + 50);
        return () => { clearInterval(id); clearTimeout(end); };
    }, [coolingUntil]);

    const card = {
        position: 'absolute', left: 16, top: 60, width: 300, zIndex: 5,
        background: '#fff', borderRadius: 16, padding: '12px 14px',
        boxShadow: '0 6px 18px rgba(0,0,0,0.18)', fontFamily: FONT_FAMILY, color: INK,
        ...(style || {}),
    };
    const small = { fontFamily: FONT_FAMILY, fontSize: 12, color: MUTED };

    // Short status sentence (announced through the always-mounted live region).
    let statusMsg = '';
    if (status === 'loading' || status === 'idle') statusMsg = L('dash_demand_loading', 'Loading demand…');
    else if (status === 'denied') statusMsg = L('dash_demand_denied', 'Your role cannot read the demand snapshot.');
    else if (status === 'error') statusMsg = L('dash_demand_error', "Can't load right now.");
    else if (status === 'off') statusMsg = L('dash_demand_off', 'The demand map is switched off (Settings › General › step 2).');
    else if (status === 'empty' || !s) statusMsg = L('dash_demand_empty', 'No snapshot yet. The publisher writes one every 10 minutes once step 2 is on.');
    const showData = Boolean(s) && !statusMsg;
    // A failed refresh with data on screen: keep the data, say so.
    const notice = status === 'stale' ? L('dash_demand_stale', "Couldn't refresh — showing the last snapshot.") : '';

    return (
        <Box style={card} role="region" aria-label={L('dash_demand_title', 'Hot zones')}>
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 32, height: 32, borderRadius: 16, background: '#FDE4DC', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Flame /></span>
                    <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 16, fontWeight: 700, color: INK }}>{L('dash_demand_title', 'Hot zones')}</Typography>
                    {showData && zones.length > 0 && !quiet ? <span style={{ minWidth: 18, height: 18, borderRadius: 9, background: ORANGE, color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px', fontFamily: FONT_FAMILY }}>{zones.length}</span> : null}
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {ageMin !== null ? <Typography style={{ ...small, fontSize: 11, color: ageMin > 40 ? '#8A5A00' : MUTED }}>{L('dash_demand_updated', 'Updated {{n}} min ago', { n: ageMin })}</Typography> : null}
                    <Button
                        size="small"
                        onClick={onRefresh}
                        disabled={refreshDisabled}
                        aria-label={L('dash_demand_refresh', 'Refresh')}
                        title={coolSecs > 0 ? L('dash_demand_refresh_wait', 'Refresh again in {{n}} s', { n: coolSecs }) : L('dash_demand_refresh', 'Refresh')}
                        style={{ minWidth: 32, minHeight: 32, padding: '2px 8px', color: refreshDisabled ? MUTED : ORANGE, fontFamily: FONT_FAMILY, textTransform: 'none', fontSize: 12 }}
                    >
                        {busy ? <CircularProgress size={14} style={{ color: ORANGE }} /> : (coolSecs > 0 ? coolSecs + ' s' : '↻ ' + L('dash_demand_refresh', 'Refresh'))}
                    </Button>
                </Box>
            </Box>

            <div aria-live="polite">
                {statusMsg ? (
                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                        <Typography style={small}>{statusMsg}</Typography>
                        {status === 'error' ? <Button size="small" onClick={onRefresh} disabled={busy} style={{ textTransform: 'none', fontFamily: FONT_FAMILY, color: ORANGE, minHeight: 32 }}>{L('dash_demand_retry', 'Retry')}</Button> : null}
                    </Box>
                ) : null}
                {notice ? <Typography style={{ ...small, marginTop: 6, color: '#8A5A00' }}>{notice}</Typography> : null}
            </div>

            {showData ? (
                <>
                    <Box style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        {[['n', L('dash_demand_now', 'Now')], ['x', nextHour ? L('dash_demand_next_hour', 'Next hour ({{hour}})', { hour: nextHour }) : L('dash_demand_next_hour_plain', 'Next hour')]].map(([key, label]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => onTab(key)}
                                aria-pressed={tab === key}
                                style={{
                                    border: 'none', cursor: 'pointer', borderRadius: 16, padding: '6px 14px', minHeight: 32, fontSize: 12, fontWeight: 700, fontFamily: FONT_FAMILY,
                                    background: tab === key ? ORANGE : '#F3F3F4', color: tab === key ? '#fff' : '#3D3D3D',
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    </Box>
                    {quiet || zones.length === 0 ? (
                        <Typography style={{ ...small, marginTop: 10 }}>
                            {quiet ? L('dash_demand_quiet', 'Quiet right now — no busy areas.') : L('dash_demand_none', 'No zones in this window.')}
                        </Typography>
                    ) : (
                        zones.map((z, i) => {
                            const tr = z.ev ? { text: L('dash_demand_expected', 'Expected'), color: EVENT_BLUE } : trendText(z.trend);
                            return (
                                <Box key={`${z.lat},${z.lng}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < zones.length - 1 ? '1px solid #F0F0F0' : 'none' }}>
                                    <span style={{ width: 24, height: 24, borderRadius: 12, background: ORANGE, color: '#fff', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none', fontFamily: FONT_FAMILY }}>{i + 1}</span>
                                    <Box style={{ flex: 1, minWidth: 0 }}>
                                        <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 14, fontWeight: 700, color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {z.en || z.am || L('dash_demand_zone_n', 'Zone {{n}}', { n: i + 1 })}
                                        </Typography>
                                        <Box style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 3 }}>
                                            <Pill level={demandHeatCodec.levelWord(z.lv)} label={levelWord(z.lv)} />
                                            <span style={{ ...small, color: tr.color, fontWeight: 500 }}>{tr.text}</span>
                                            <span style={small}>· {driversText(z.drivers)}</span>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })
                    )}
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                        {[['warming', L('dash_demand_level_warming', 'Warming')], ['busy', L('dash_demand_level_busy', 'Busy')], ['very', L('dash_demand_level_very', 'Very busy')]].map(([k, label]) => (
                            <span key={k} style={{ ...small, fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ width: 12, height: 12, borderRadius: 3, background: LEVEL_STYLE[k].dot, border: '1px solid rgba(0,0,0,0.15)', display: 'inline-block' }} />{label}
                            </span>
                        ))}
                    </Box>
                </>
            ) : null}

            {isAdmin && meta && (status === 'ok' || status === 'stale') ? (
                <Typography style={{ ...small, fontSize: 11, marginTop: 10 }}>
                    {L('dash_demand_health', 'Published {{time}} · {{cells}} cells · live {{live}} · α {{alpha}} · ref {{ref}} req/h', {
                        time: meta.lastRun ? moment(meta.lastRun).format('HH:mm') : '—',
                        cells: meta.cells && Number.isFinite(meta.cells.n) ? meta.cells.n : '—',
                        live: Number.isFinite(meta.liveTotal) ? Math.round(meta.liveTotal * 10) / 10 : '—',
                        alpha: Number.isFinite(meta.alpha) ? meta.alpha : '—',
                        ref: Number.isFinite(meta.ref) ? meta.ref : '—',
                    })}
                </Typography>
            ) : null}
            <Typography style={{ ...small, fontSize: 11, marginTop: 8 }}>
                {L('dash_demand_footer', 'What drivers see · refreshes every 10 min while this tab is open')}
            </Typography>
        </Box>
    );
}
